import { useEffect, useMemo, useState } from 'react';
import { CONFIG } from '../../../../config';
import { ProductDetailsFragment, SizeInventoryFragment } from '../../../../generated/types';
import { APPLE_PAY_CONFIG } from '../../../ApplePay';
import { useCountry } from '../../../CountrySelector';

type CartResponse = {
  data: {
    id?: string;
    attributes: {
      apple_summary: {
        data: {
          currency: string;
          total: ApplePayJS.ApplePayLineItem;
          line_items: ApplePayJS.ApplePayLineItem[];
          shipping_contact: ApplePayJS.ApplePayPaymentContact;
          shipping_methods: ApplePayJS.ApplePayShippingMethod[];
        };
      };
    };
  }[];
};

export const useCreateApplePayQuickBuyCart = (
  variant: ProductDetailsFragment,
  size: SizeInventoryFragment | null
) => {
  const [data, setData] = useState<undefined | ApplePayJS.ApplePayPaymentRequest>();
  const [loading, setLoading] = useState(false);
  const [cartId, setCartId] = useState<undefined | string>();
  const {
    country: { alpha2Code: countryCode }
  } = useCountry();
  const [requestedSize, setRequestedSize] = useState<undefined | string>();

  const currentSelectedSize: undefined | string = useMemo(() => {
    if (!size) return;

    if (!size.size) return;

    return size.size;
  }, [size]);

  useEffect(() => {
    if (!currentSelectedSize) return;

    if (loading) return;

    if (data) return;

    setLoading(true);

    fetch(`${CONFIG.MODA_API_URL}/v3.5/line_items`, {
      method: 'POST',
      mode: 'cors',
      cache: 'no-cache',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/vnd.api+json' },
      redirect: 'follow',
      referrer: 'no-referrer',
      body: JSON.stringify({
        data: {
          attributes: {
            availability: variant.availability,
            country_code: 'US',
            customization: null,
            position: 0,
            product_id: variant.productId,
            size: currentSelectedSize,
            target: 'PDP--QuickBuy',
            trunkshow_id: null,
            variant_id: variant.masterVariantId
          },
          type: 'line_items'
        }
      })
    })
      .then(response => response.json())
      .then(
        ({
          data: [
            {
              id,
              attributes: {
                apple_summary: {
                  data: {
                    currency,
                    total,
                    line_items: lineItems,
                    shipping_contact: shippingContact,
                    shipping_methods: shippingMethods
                  }
                }
              }
            }
          ]
        }: CartResponse) => {
          setData({
            countryCode,
            ...APPLE_PAY_CONFIG,
            currencyCode: currency,
            total,
            lineItems,
            shippingContact,
            shippingMethods
          });

          setRequestedSize(currentSelectedSize);

          setCartId(id);

          setLoading(false);
        }
      );
  }, [
    countryCode,
    currentSelectedSize,
    data,
    loading,
    size,
    variant.availability,
    variant.masterVariantId,
    variant.productId
  ]);

  useEffect(() => {
    if (!currentSelectedSize) return;

    if (!requestedSize) return;

    if (currentSelectedSize !== requestedSize) {
      setRequestedSize(undefined);

      setData(undefined);
    }
  }, [currentSelectedSize, data, requestedSize, size]);

  return { data, loading, cartId };
};
